import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@cyphex/ui';
import { MAX_VOICE_MS, WAVEFORM_BUCKETS } from '../constants';
import { bucketsFromPcm, levelFromTimeDomain } from '../lib/waveform';

export interface VoiceClipResult {
  blob: Blob;
  durationMs: number;
  waveform: number[];
}

interface UseVoiceRecorderResult {
  isRecording: boolean;
  elapsedMs: number;
  /** 0..100 live meter, drives the recording bars. */
  level: number;
  start: () => Promise<void>;
  stop: () => Promise<VoiceClipResult | null>;
  cancel: () => void;
}

/** MediaRecorder capture with a live meter and a real 64-bucket waveform. */
export function useVoiceRecorder(): UseVoiceRecorderResult {
  const [isRecording, setRecording] = useState(false);
  const [elapsedMs, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const levels = useRef<number[]>([]);
  const startedAt = useRef(0);
  const raf = useRef(0);

  const teardown = useCallback(() => {
    cancelAnimationFrame(raf.current);
    recorder.current?.stream.getTracks().forEach((track) => track.stop());
    recorder.current = null;
    setRecording(false);
    setLevel(0);
  }, []);

  useEffect(() => teardown, [teardown]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      context.createMediaStreamSource(stream).connect(analyser);
      const buffer = new Uint8Array(analyser.fftSize);

      chunks.current = [];
      levels.current = [];
      startedAt.current = Date.now();
      const media = new MediaRecorder(stream);
      media.ondataavailable = (event) => chunks.current.push(event.data);
      media.start(250);
      recorder.current = media;
      setRecording(true);

      const tick = (): void => {
        analyser.getByteTimeDomainData(buffer);
        const value = levelFromTimeDomain(buffer);
        levels.current.push(value / 100);
        setLevel(value);
        const elapsed = Date.now() - startedAt.current;
        setElapsed(elapsed);
        if (elapsed >= MAX_VOICE_MS) media.stop();
        else raf.current = requestAnimationFrame(tick);
      };
      raf.current = requestAnimationFrame(tick);
    } catch {
      toast.error('Microphone unavailable. Check permissions.');
    }
  }, []);

  const stop = useCallback(async (): Promise<VoiceClipResult | null> => {
    const media = recorder.current;
    if (!media) return null;
    const durationMs = Date.now() - startedAt.current;
    const blob = await new Promise<Blob>((resolve) => {
      media.onstop = () => resolve(new Blob(chunks.current, { type: media.mimeType || 'audio/webm' }));
      media.stop();
    });
    const waveform = bucketsFromPcm(Float32Array.from(levels.current), WAVEFORM_BUCKETS);
    teardown();
    setElapsed(0);
    return { blob, durationMs, waveform };
  }, [teardown]);

  const cancel = useCallback(() => {
    recorder.current?.stop();
    teardown();
    setElapsed(0);
  }, [teardown]);

  return { isRecording, elapsedMs, level, start, stop, cancel };
}
